import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from "~/components/ui/carousel";
import { forwardRef, useImperativeHandle, useRef } from "react";
import type { CarouselApi } from "~/components/ui/carousel";
import { cn } from "~/lib/utils";

export interface Tab {
  id: string;
  label: string;
}

interface OverflowTabsProps {
  tabs: Tab[];
  activeTabId: string;
  onTabChange: (tabId: string) => void;
  className?: string;
}

export interface OverflowTabsRef {
  scrollToTab: (tabId: string) => void;
}

export const OverflowTabs = forwardRef<OverflowTabsRef, OverflowTabsProps>(
  ({ tabs, activeTabId, onTabChange, className = "" }, ref) => {
    const carouselApi = useRef<CarouselApi>(null);

    useImperativeHandle(ref, () => ({
      scrollToTab: (tabId: string) => {
        const tabIndex = tabs.findIndex(tab => tab.id === tabId);
        if (tabIndex !== -1 && carouselApi.current) {
          carouselApi.current.scrollTo(tabIndex);
        }
      },
    }));

    const handleTabChange = (tabId: string) => {
      onTabChange(tabId);
    };

    return (
      <div className={`relative ${className}`}>
        <Carousel
          opts={{
            align: "start",
            containScroll: "trimSnaps",
            dragFree: true,
            watchDrag: true,
            watchResize: true,
            slidesToScroll: 1,
            duration: 15,
          }}
          className="w-full"
          setApi={api => (carouselApi.current = api)}
        >
          <div className="flex items-center">
            {/* Tab content area */}
            <div className="flex-1 overflow-hidden">
              <CarouselContent className="-ml-3">
                {tabs.map(tab => (
                  <CarouselItem key={tab.id} className="basis-auto pl-3">
                    <button
                      onClick={() => handleTabChange(tab.id)}
                      className={cn(
                        "flex-shrink-0",
                        "flex items-center justify-center",
                        "h-[32px] rounded-[12px] border px-[16px] py-[8px]",
                        "transition-all duration-200",
                        "text-[14px] leading-[20px] font-medium text-[#171717]",
                        activeTabId === tab.id
                          ? "border-[#0000000D] bg-[#FFFEDD] font-bold"
                          : "border-[#0000001A] bg-white font-medium"
                      )}
                    >
                      {tab.label}
                    </button>
                  </CarouselItem>
                ))}
              </CarouselContent>
            </div>

            {/* Right padding for navigation buttons - always space for both */}
            <div className="w-20 flex-shrink-0" />
          </div>

          {/* Always show both navigation buttons with disabled states */}
          <CarouselPrevious
            variant="carouselNav"
            size="icon"
            customPosition
            className="top-1/2 right-11 h-8 w-8 -translate-y-1/2 shadow-none"
          />
          <CarouselNext
            variant="carouselNav"
            size="icon"
            customPosition
            className="top-1/2 right-1 h-8 w-8 -translate-y-1/2 shadow-none"
          />
        </Carousel>
      </div>
    );
  }
);

OverflowTabs.displayName = "OverflowTabs";
